'use client';
import { load } from 'cheerio/lib/slim';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import Content from './Content';
import { motion, useScroll } from 'framer-motion';

const ArticleSection = ({ content, guide, faqHeading, faqArr }) => {
  const [lightMode, setLightMode] = useState(true); // Default to light mode

  useEffect(() => {
    if (lightMode) {
      document.body.style.backgroundColor = 'white';
      document.body.style.color = 'black';
    } else {
      document.body.style.backgroundColor = '';
      document.body.style.color = 'white';
    }

    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, [lightMode]);

  const $ = load(content ?? '');

  const [selectedSection, setSelectedSection] = useState('');
  const [scrolling, setScrolling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [sectionHeadings, setSectionHeadings] = useState([]);
  const element = useRef(null);
  const { scrollYProgress } = useScroll({
    target: element,
  });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const topOffset =
        element.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: topOffset, behavior: 'smooth' });
    }
  };

  // Extract all h1 tags
  const h1Tags = $('h1');

  const allValuesNull = Object.values(guide).every((value) => value === null);

  //Download File
  const handleDownload = async () => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}/assets/${guide.file}`
      );
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.download = guide.file
        .toLowerCase() // Convert to lowercase
        .replace(/\s+/g, '-')
        .replace('/', '@')
        // .replace(/[^\w-]+/g, '') // Remove non-word characters (excluding hyphens)
        .replace(/--+/g, '-')
        .replace('.', '-');

      link.href = blobUrl;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY;

      if (currentPosition > scrollTop + 3) {
        setScrolling(true);
      } else if (currentPosition < scrollTop - 3) {
        setScrolling(false);
      }
      setScrollTop(currentPosition);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollTop]);

  useEffect(() => {
    const headings = h1Tags.map((index, element) => {
      const sectionId = `section-${index + 1}`;
      return sectionId;
    });
    setSectionHeadings(headings);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY + 100;

      for (let i = sectionHeadings.length; i >= 1; i--) {
        const element = document.getElementById(sectionHeadings[i - 1]);
        if (element) {
          // const elementOffset = element.offsetTop;
          const topOffset =
            element.getBoundingClientRect().top + window.pageYOffset - 100;
          if (currentScrollTop >= topOffset) {
            setSelectedSection(sectionHeadings[i - 1]);
            break;
          }
        } else {
          const element = document.getElementById('blogFaq');
          if (element) {
            const elementOffset = element.offsetTop;
            if (currentScrollTop >= elementOffset) {
              setSelectedSection('blogFaq');
              break;
            }
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sectionHeadings]);

  useEffect(() => {
    // Scroll to the section if `h1Tags` is populated and there's a hash in the URL
    setTimeout(() => {
      if (window.location.hash) {
        const sectionId = window.location.hash.substring(1); // Remove the '#' from the hash
        setSelectedSection(sectionId);
      }
    }, 200);
  }, [sectionHeadings]);

  // Dynamically add the Instagram and TikTok embed scripts
  useEffect(() => {
    const instagramScript = document.createElement('script');
    instagramScript.src = 'https://www.instagram.com/embed.js';
    instagramScript.async = true;

    const tiktokScript = document.createElement('script');
    tiktokScript.src = 'https://www.tiktok.com/embed.js';
    tiktokScript.async = true;

    document.body.appendChild(instagramScript);
    document.body.appendChild(tiktokScript);

    return () => {
      // Clean up the scripts if the component is unmounted
      document.body.removeChild(instagramScript);
      document.body.removeChild(tiktokScript);
    };
  }, []);

  return (
    <div ref={element} className="w-full flex-col lg:flex-row flex gap-2">
      <div
        style={{ background: lightMode ? 'black' : 'white' }}
        onClick={() => {
          setLightMode(!lightMode);
        }}
        className="w-12 h-12 cursor-pointer rounded-full flex items-center justify-center fixed bottom-4 right-4"
      >
        {lightMode ? (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              fill="white"
              viewBox="0 0 256 256"
            >
              <path d="M235.54,150.21a104.84,104.84,0,0,1-37,52.91A104,104,0,0,1,32,120,103.09,103.09,0,0,1,52.88,57.48a104.84,104.84,0,0,1,52.91-37,8,8,0,0,1,10,10,88.08,88.08,0,0,0,109.8,109.8,8,8,0,0,1,10,10Z"></path>
            </svg>
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              fill="#000000"
              viewBox="0 0 256 256"
            >
              <path d="M120,40V32a8,8,0,0,1,16,0v8a8,8,0,0,1-16,0Zm8,24a64,64,0,1,0,64,64A64.07,64.07,0,0,0,128,64ZM58.34,69.66A8,8,0,0,0,69.66,58.34l-8-8A8,8,0,0,0,50.34,61.66Zm0,116.68-8,8a8,8,0,0,0,11.32,11.32l8-8a8,8,0,0,0-11.32-11.32ZM192,72a8,8,0,0,0,5.66-2.34l8-8a8,8,0,0,0-11.32-11.32l-8,8A8,8,0,0,0,192,72Zm5.66,114.34a8,8,0,0,0-11.32,11.32l8,8a8,8,0,0,0,11.32-11.32ZM40,120H32a8,8,0,0,0,0,16h8a8,8,0,0,0,0-16Zm88,88a8,8,0,0,0-8,8v8a8,8,0,0,0,16,0v-8A8,8,0,0,0,128,208Zm96-88h-8a8,8,0,0,0,0,16h8a8,8,0,0,0,0-16Z"></path>
            </svg>
          </>
        )}
      </div>
      <motion.div
        className={`fixed left-0 origin-left w-full top-0 z-50 h-[4px] bg-[#00e6e6]`}
        style={{ scaleX: scrollYProgress }}
      />
      <div className="md:w-[200px] w-full flex flex-col md:py-5 py-0  md:px-0 md:border-none md:border-y-2 border-none border-black/30">
        <div
          className={`md:sticky relative w-full md:top-5 transition-all duration-300`}
        >
          <h2 className="font-semibold md:text-[18px] text-[14px] font-proxima-font">
            In This Article
          </h2>
          <ul className=" mt-2 ">
            {h1Tags.map((index, element) => {
              const sectionText = $(element).text();
              const sectionId = `section-${index + 1}`;

              // Set the id attribute on the h1 tag
              $(element).attr('id', sectionId);

              return (
                <li
                  key={index}
                  className={`flex relative gap-2 mt-[0.5px] items-center `}
                >
                  <a
                    href={`#${sectionId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedSection(sectionId);
                      scrollToSection(sectionId);
                    }}
                    className={`${
                      selectedSection === sectionId
                        ? 'opacity-100'
                        : 'opacity-60'
                    } font-semibold cursor-pointer text-[14px] font-proxima-font gap-2 my-1 px-2 block md:w-[200px] w-full whitespace-normal break-words`}
                  >
                    {/* <span>{index + 1}.</span> */}
                    {sectionText}
                  </a>
                </li>
              );
            })}
            <>
              {faqHeading && faqArr && (
                <li className={`flex relative gap-2 mt-[0.5px] items-center `}>
                  <a
                    href={`#blogFaq`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedSection('blogFaq');
                      scrollToSection('blogFaq');
                    }}
                    className={`${
                      selectedSection === 'blogFaq'
                        ? 'text-black '
                        : 'text-[#666D80]'
                    } font-semibold cursor-pointer text-[14px] font-proxima_font text-wrap flex gap-2 my-1 px-2`}
                  >
                    {/* <span>{h1Tags?.length + 1}.</span> */}
                    FAQs
                  </a>
                </li>
              )}
            </>
          </ul>
        </div>
      </div>

      <div className="w-full flex flex-col gap-3 overflow-hidden items-center">
        <Content content={content} />

        {allValuesNull ? (
          <></>
        ) : (
          <div className=" flex flex-col  md:flex-row gap-5 items-center p-5 rounded-xl bg-gradient-to-l from-[#F5812D] to-[#F9A569] text-white">
            {guide.last_image && (
              <Image
                src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${guide.last_image}?quality=10`}
                alt="guideImg"
                className=" w-40 h-40 object-contain"
                width={400}
                height={400}
              />
            )}
            <div className="flex flex-col gap-5 justify-center">
              {guide.last_heading && (
                <p className="text-2xl font-semibold">{guide.last_heading}</p>
              )}
              {guide.last_description && (
                <p className="text-sm font-proxima_font">
                  {guide.last_description}
                </p>
              )}
              {guide.file && (
                <button
                  onClick={handleDownload}
                  className="w-full px-5 rounded-full bg-white"
                >
                  {guide.btn_text && (
                    <p className="text-black text-lg font-semibold p-2 text-center">
                      {guide.btn_text}
                    </p>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleSection;
